/** هدر + قابِ صفحه. هرچی در همه‌ی صفحه‌ها مشترکه اینجاست. */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { brand, t, useLocale } from '@/core/lib/i18n';
import { useSession } from '@/core/session';
import { useTheme } from '@/ui/theme';
import { useNotifIndicators } from '@/features/notifications/useNotifIndicators';
import { ForegroundNotices } from '@/features/notifications/ForegroundNotices';
import { Avatar } from '@/ui/kit';
import { EventPlusIcon } from '@/ui/icons';
import { useIsNarrow } from '@/ui/useIsNarrow';
import { useNewSignal } from '@/features/signals/NewSignalGuard';
import { TourGuide } from '@/features/tour/TourGuide';
import { useTour } from '@/core/lib/tour';
import { HeaderTabs } from './HeaderTabs';
import { MobileMenu } from './MobileMenu';
import { returnHere } from '@/features/auth/returnHere';

export function Shell({ children }: { children: React.ReactNode }) {
  useLocale(); // متنِ هدر با تعویضِ زبان تازه بشه
  const { isDark, toggle } = useTheme();
  const { session, profile, signOut } = useSession();
  const nav = useNavigate();
  const location = useLocation();
  // «هنوز دوستی نداری» — یه‌بار، قبل از باز شدنِ فرم. نگاه کن به NewSignalGuard.
  const newSignal = useNewSignal();
  /**
   * روی صفحه‌ی باریک کلِ ناوبری عوض می‌شه، نه اینکه فقط کوچک‌تر بشه: تب‌های
   * هدر و دکمه‌هاش می‌رن داخلِ منوی سه‌خط. علتش هم همینه که این تصمیم
   * جاوااسکریپتیه نه CSSی — نگاه کن به `ui/useIsNarrow.ts`.
   */
  const narrow = useIsNarrow();
  // فقط وقتی نشستی هست معنی داره؛ خودِ هوک با profile نال هم امن کار می‌کنه.
  const { dots } = useNotifIndicators();
  /**
   * تورِ کاربرِ تازه (WHOSUP-83). هدر هم لازمش دارد، نه فقط خودِ کارت: هر قدم
   * باید به چیزی که درباره‌اش حرف می‌زند **اشاره** کند، و روی وب آن اشاره
   * یعنی برجسته شدنِ همان لینکِ هدر (کلاسِ `tour-point`).
   */
  const tour = useTour();
  const point = (tab: string): string => (tour?.step.tab === tab ? ' tour-point' : '');

  return (
    <>
      {/* فقط تزئینی — لکه‌های محوِ گرم که پوسته‌ی Golden Hour رو می‌سازن. */}
      <div className="blobs" aria-hidden="true">
        <span className="blob b1" /><span className="blob b2" /><span className="blob b3" />
      </div>

      <header className="app-header">
        <div className="bar">
          {/* روی گوشی این دکمه جای همه‌چیزِ سمتِ راستِ هدر **و** نوارِ کناری
              رو می‌گیره؛ روی دسکتاپ اصلاً ساخته نمی‌شه. */}
          {/* روی صفحه‌ی باریک همه‌ی مقصدهای تور پشتِ همین دکمه‌اند، پس اشاره‌ی
              هر قدمی هم به خودِ دکمه است — وگرنه تور از چیزی حرف می‌زد که
              اصلاً روی صفحه دیده نمی‌شود. */}
          {narrow ? <MobileMenu dots={dots} guest={!session} pointing={!!tour} /> : null}

          {/*
            نشانِ کلامیِ برند — از `brand()` می‌آید، نه از متنِ سفت.

            ⚠️ تاریخچه‌اش دلیلِ همین شکل است: قبلاً `whos<b>up</b>` بود و چون
            رشته با تگ شکسته شده بود، هیچ جستجویی پیدایش نکرد و از هر سه تسکِ
            تغییرِ نام جا ماند — در حالی که دیده‌شده‌ترین جای برند در کلِ سایت
            است. بعد که دستی «Pals & Plans» شد، برندِ دومِ outami.com همان‌جا
            نامِ اشتباه را نشان داد. کامنتِ «دستی ببین» کافی نبود؛ باید از
            منبعِ واحد می‌آمد.

            `<b>` رنگِ برند می‌گیرد (base.css). فقط «&» را برجسته می‌کنیم تا
            همان لهجه‌ی کهربایی آیکون روی متن تکرار شود؛ نامی که «&» ندارد
            ساده رندر می‌شود — مثلِ خودِ لوگویش که لهجه‌اش در حلقه است.
          */}
          <Link to="/" className="logo-word" style={{ textDecoration: 'none', color: 'inherit' }}>
            {brand().split(/(&)/).map((part, i) => (
              part === '&' ? <b key={i}>&amp;</b> : <React.Fragment key={i}>{part}</React.Fragment>
            ))}
          </Link>

          {/* تب‌های اصلی — جانشینِ نوارِ کناریِ هاوری (WHOSUP-157). روی گوشی
              نیستن؛ همون ردیف‌ها داخلِ منوی سه‌خط‌ان. */}
          {narrow ? null : <HeaderTabs dots={dots} guest={!session} point={tour?.step.tab ?? null} />}

          {/* روی دسکتاپ هدر طبیعی‌ترین جای این کارهاست چون همیشه دمِ دسته.
              روی گوشی همه‌شون داخلِ منوی سه‌خط‌ان — یک ردیفِ ۶۶ پیکسلی جای
              سه تا دکمه‌ی متن‌دار به‌علاوه‌ی آواتار و تم نداره، و تلاش برای
              جا دادنشون همونی بود که متنِ دکمه‌ها رو سه‌خطی می‌شکست. */}
          {narrow ? null : (
            <div className="header-right">
              {session ? (
                <>
                  <Link to="/suggest" className="btn ghost">
                    <EventPlusIcon />
                    <span>{t('suggestTitle')}</span>
                  </Link>
                  {/* لینکه، نه دکمه: آدرسِ واقعی باید توی نوارِ وضعیت و
                      «باز کردن در تبِ جدید» بمونه. فقط کلیکِ عادی از پشتِ
                      هشدارِ «هنوز دوستی نداری» رد می‌شه — کلیک با ctrl/⌘
                      دستِ خودِ مرورگر می‌مونه، مثلِ هر لینکِ دیگه. */}
                  <Link to="/signal/new" className={`btn fill${point('signals')}`} title={t('whoIsIn')}
                    onClick={(e) => {
                      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
                      e.preventDefault();
                      newSignal(() => nav('/signal/new'));
                    }}>
                    {t('newSignal')}
                  </Link>
                </>
              ) : (
                // مهمان همین دو دکمه رو می‌بینه، نه یه هدرِ خالی: خودِ *بودنِ*
                // این کارها بخشی از معرفیِ محصوله. مقصدشون /auth است با آدرسِ
                // واقعی توی state، پس بعدِ ورود دقیقاً همون‌جا می‌رسه که زده بود
                // — نه صفحه‌ی اول (نگاه کن به AuthRoute در routes.tsx).
                <>
                  <Link to="/auth" state={{ from: '/suggest' }} className="btn ghost">
                    <EventPlusIcon />
                    <span>{t('suggestTitle')}</span>
                  </Link>
                  <Link to="/auth" state={{ from: '/signal/new' }} className="btn ghost"
                    title={t('whoIsIn')}>
                    {t('newSignal')}
                  </Link>
                  {/* تنها دکمه‌ی پُر برای مهمان — کارِ اصلی‌ای که ازش می‌خوایم.
                      بعد از ورود به همین صفحه برمی‌گرده (WHOSUP-359). */}
                  <Link to="/auth" state={returnHere(location)} className="btn fill">{t('authSignIn')}</Link>
                </>
              )}
              <button
                type="button"
                className="iconbtn"
                onClick={toggle}
                aria-label={t('themeTitle')}
                title={t('themeTitle')}
              >
                {isDark ? '☀' : '☾'}
              </button>
              {profile ? (
                <Link to="/settings" className={point('settings').trim() || undefined}
                  aria-label={t('settings')} title={t('settings')}>
                  <Avatar url={profile.avatarUrl} name={profile.displayName} size={30} />
                </Link>
              ) : null}
              {session ? (
                <button type="button" className="btn ghost" onClick={() => void signOut()}>
                  {t('signOut')}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </header>

      {/* نوارِ کناریِ هاوری این‌جا بود (WHOSUP-157 حذفش کرد): سیگنال‌ها و
          دوستان حالا تبِ خودشون رو توی هدر دارن و صفحه‌ی کامل باز می‌کنن. */}
      <main>{children}</main>

      {/* اعلانکِ خبرِ تازه وقتی خودِ سایت جلوی چشم است — از همان کانالِ
          زنده‌ی useNotifIndicators بالا تغذیه می‌شود (WHOSUP-71). */}
      <ForegroundNotices />

      {/* تورِ کاربرِ تازه (WHOSUP-83) — بیرونِ `main` چون روی هر صفحه‌ای که
          کاربر باشد همان کارت است، و برای کاربرِ قدیمی چیزی رندر نمی‌کند. */}
      <TourGuide />
    </>
  );
}
